/**
 * Thanh phan giao dien dung chung.
 *
 * Moi thanh phan doc mau tu token, khong viet ma mau truc tiep — de doi thuong hieu
 * chi phai sua tep tokens.css.
 */

import type { ButtonHTMLAttributes, InputHTMLAttributes, JSX, ReactNode } from 'react';
import styles from './ui.module.css';

// ---------------------------------------------------------------------------

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  fullWidth?: boolean;
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  fullWidth = false,
  children,
  disabled,
  className,
  ...rest
}: ButtonProps): JSX.Element {
  return (
    <button
      className={[
        styles.button,
        styles[`button--${variant}`],
        styles[`button--${size}`],
        fullWidth ? styles['button--full'] : '',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
      disabled={disabled || loading}
      // Bao cho trinh doc man hinh biet nut dang xu ly, khong chi doi hinh thuc.
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading && <span className={styles.spinner} aria-hidden="true" />}
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string;
  error?: string;
}

export function Field({ label, hint, error, id, ...rest }: FieldProps): JSX.Element {
  const inputId = id ?? `field-${label.replace(/\s+/g, '-').toLowerCase()}`;
  const describedBy = error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined;

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={inputId}>
        {label}
      </label>
      <input
        id={inputId}
        className={[styles.input, error ? styles['input--error'] : ''].filter(Boolean).join(' ')}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        {...rest}
      />
      {error ? (
        <span id={`${inputId}-error`} className={styles.errorText} role="alert">
          {error}
        </span>
      ) : hint ? (
        <span id={`${inputId}-hint`} className={styles.hint}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------

export function Card({
  children,
  title,
  action,
  padded = true,
}: {
  children: ReactNode;
  title?: string;
  action?: ReactNode;
  padded?: boolean;
}): JSX.Element {
  return (
    <section className={styles.card}>
      {(title || action) && (
        <header className={styles.cardHeader}>
          {title && <h2 className={styles.cardTitle}>{title}</h2>}
          {action}
        </header>
      )}
      <div className={padded ? styles.cardBody : undefined}>{children}</div>
    </section>
  );
}

// ---------------------------------------------------------------------------

/**
 * Danh sach ly do tu choi.
 *
 * May chu tra ve TOAN BO ly do chu khong dung o ly do dau tien, va giao dien phai
 * hien thi het: bat nguoi choi sua tung loi mot roi thu lai la cach nhanh nhat de
 * ho bo cuoc giua chung.
 */
export function ErrorList({ messages }: { messages: string[] }): JSX.Element | null {
  if (messages.length === 0) return null;
  return (
    <div className={styles.errorBox} role="alert">
      {messages.length === 1 ? (
        <p>{messages[0]}</p>
      ) : (
        <ul className={styles.errorItems}>
          {messages.map((m, i) => (
            <li key={i}>{m}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

export function Badge({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: 'neutral' | 'positive' | 'negative' | 'warning' | 'brand';
}): JSX.Element {
  return <span className={[styles.badge, styles[`badge--${tone}`]].join(' ')}>{children}</span>;
}

// ---------------------------------------------------------------------------

/** Khoi cho du lieu — giu dung cho de bo cuc khong nhay khi du lieu ve. */
export function Skeleton({ height = 20, width }: { height?: number; width?: number | string }): JSX.Element {
  return (
    <span
      className={styles.skeleton}
      style={{ height, width: width ?? '100%' }}
      aria-hidden="true"
    />
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}): JSX.Element {
  return (
    <div className={styles.empty}>
      <p className={styles.emptyTitle}>{title}</p>
      {description && <p className={styles.emptyText}>{description}</p>}
      {action}
    </div>
  );
}

// ---------------------------------------------------------------------------

/** So tien: luon dung chu deu chan de cot so thang hang. */
export function Amount({
  value,
  currency,
  tone,
  size = 'md',
}: {
  value: string;
  currency?: string;
  tone?: 'positive' | 'negative';
  size?: 'sm' | 'md' | 'lg';
}): JSX.Element {
  return (
    <span
      className={[
        'numeric',
        styles.amount,
        styles[`amount--${size}`],
        tone ? styles[`amount--${tone}`] : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {value}
      {currency && <span className={styles.amountCurrency}>{currency}</span>}
    </span>
  );
}
